// Conquistas (badges): cálculo e grade.
// Extraído de App.js sem alteração de comportamento.
import React from "react";
import { useTheme } from "../lib/theme";
import { NavIcon } from "./icons";
import { BADGES } from "../lib/constants";
import { useNoteCounts } from "../lib/noteStats";

// ── SCREENS ──────────────────────────────────────────────────────────────────

// ── BADGES / CONQUISTAS ───────────────────────────────────────────────────────

export function calcBadges(totalSec, simSaved, questEntries, extra) {
  const unlocked = new Set();
  const totalH = (totalSec||0) / 3600;
  if (totalH >= 1)   unlocked.add("primeiros_passos");
  if (totalH >= 10)  unlocked.add("dedicado");
  if (totalH >= 50)  unlocked.add("guerreiro");
  if (totalH >= 100) unlocked.add("veterano");
  if (totalH >= 500) unlocked.add("elite");
  const simCount = (simSaved||[]).length;
  if (extra && extra.force) extra.force.forEach(id => unlocked.add(id));
  if (simCount >= 1)  unlocked.add("simulado_1");
  if (simCount >= 5)  unlocked.add("simulado_5");
  if (simCount >= 10) unlocked.add("simulado_10");
  if (extra && typeof extra.streakBest === "number") {
    if (extra.streakBest >= 3)  unlocked.add("streak_3");
    if (extra.streakBest >= 7)  unlocked.add("streak_7");
    if (extra.streakBest >= 30) unlocked.add("streak_30");
  } else try {
    const sessions = JSON.parse(localStorage.getItem("sf_sessions")||"[]");
    const days = new Set(sessions.map(s=>s.ts?new Date(s.ts).toISOString().slice(0,10):"").filter(Boolean));
    let streak = 0; let maxStreak = 0; let cur = new Date(); cur.setHours(0,0,0,0);
    for (let i=0;i<365;i++) {
      const k = cur.toISOString().slice(0,10);
      if (days.has(k)) { streak++; maxStreak = Math.max(maxStreak, streak); }
      else { if (i>0) break; }
      cur.setDate(cur.getDate()-1);
    }
    if (maxStreak >= 3)  unlocked.add("streak_3");
    if (maxStreak >= 7)  unlocked.add("streak_7");
    if (maxStreak >= 30) unlocked.add("streak_30");
  } catch(e){}
  const nc = extra && extra.nc;
  if (nc) {
    [[1,"resumo_1"],[10,"resumo_10"],[50,"resumo_50"]].forEach(([n,id]) => { if (nc.notes >= n) unlocked.add(id); });
    [[1,"card_1"],[100,"card_100"],[500,"card_500"]].forEach(([n,id]) => { if (nc.cards >= n) unlocked.add(id); });
    [[1,"rev_1"],[100,"rev_100"],[500,"rev_500"]].forEach(([n,id]) => { if (nc.reviewed >= n) unlocked.add(id); });
  }
  const bestPct = (simSaved||[]).reduce((best,s) => {
    if (!s.total || s.total===0) return best;
    return Math.max(best, ((s.net||0)/s.total)*100);
  }, 0);
  if (bestPct >= 70) unlocked.add("aproveitamento_70");
  if (bestPct >= 90) unlocked.add("aproveitamento_90");
  return unlocked;
}

export function BadgesGrid({ totalSec, simSaved, questEntries, extra, compact=false, loading=false, uid }) {
  const t = useTheme();
  const nc = useNoteCounts(uid);
  // Enquanto o histórico carrega, nenhuma conquista aparece marcada: evita mostrar só parte da lista e depois completar.
  const unlocked = loading || nc === undefined ? new Set() : calcBadges(totalSec, simSaved, questEntries, { ...(extra || {}), nc });
  const [hovered, setHovered] = React.useState(null);

  if (compact) {
    // Dashboard: smaller icons row with tooltip
    return (
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(40px, 1fr))", gap:"0.5rem", justifyItems:"center" }}>
        {BADGES.map(b => {
          const on = unlocked.has(b.id);
          return (
            <div key={b.id} onMouseEnter={()=>setHovered(b.id)} onMouseLeave={()=>setHovered(null)}
              style={{ position:"relative", width:40, height:40, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"1.25rem", background: on ? t.acDim : t.bgInner,
                border:`2px solid ${on ? t.ac : t.borderIn}`,
                opacity: on ? 1 : 0.6, filter: on ? "none" : "grayscale(0.5)", transition:"all 0.15s", cursor:"default", color: on ? t.ac : t.textDim,
              }}>
              <NavIcon id={"b_" + b.id} size={22} />
              {on && <div style={{ position:"absolute", bottom:2, right:2, width:6, height:6, borderRadius:"50%", background:t.ac }}/>}
              {hovered===b.id && (
                <div style={{ position:"absolute", bottom:"110%", left:"50%", transform:"translateX(-50%)", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:8, padding:"0.5rem 0.75rem", whiteSpace:"nowrap", zIndex:99, pointerEvents:"none", boxShadow:"0 8px 24px #0008" }}>
                  <p style={{ margin:0, fontSize:"0.8rem", fontWeight:700, color: on?t.ac:t.text }}>{b.label}</p>
                  <p style={{ margin:0, fontSize:"0.7rem", color: t.textMid, marginTop:"0.15rem" }}>{b.desc}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Perfil: cards com ícone + nome + descrição
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))", gap:"0.6rem" }}>
      {BADGES.map(b => {
        const on = unlocked.has(b.id);
        return (
          <div key={b.id}
            style={{ position:"relative", borderRadius:10, padding:"0.8rem 0.6rem", display:"flex", flexDirection:"column", alignItems:"center", gap:"0.35rem", textAlign:"center",
              background: on ? t.acDim : t.bgInner,
              border:`1.5px solid ${on ? t.ac : t.borderIn}`,
              boxShadow: on ? `0 0 10px ${t.ac}30` : "none",
              opacity: on ? 1 : 0.35, filter: on ? "none" : "grayscale(1)", transition:"all 0.15s", cursor:"default",
            }}>
            {on && <div style={{ position:"absolute", top:6, right:7, fontSize:"0.55rem", color:t.ac, fontWeight:900, letterSpacing:"0.05em" }}>✦</div>}
            <span style={{ lineHeight:1, display:"flex", color: on ? t.ac : t.textDim }}><NavIcon id={"b_" + b.id} size={30} /></span>
            <p style={{ margin:0, fontSize:"0.7rem", fontWeight:700, color: on ? t.ac : t.textDim, lineHeight:1.2 }}>{b.label}</p>
            <p style={{ margin:0, fontSize:"0.6rem", color: on ? t.textDim : t.textFaint, lineHeight:1.3 }}>{b.desc}</p>
          </div>
        );
      })}
    </div>
  );
}
